import axios from "axios";

const useApi = () => {
  const env = process.env.REACT_APP_ENV || "development";

  const baseURLMapping = {
    production: process.env.REACT_APP_BASE_URL_PRODUCTION,
    staging: process.env.REACT_APP_BASE_URL_STAGING,
    development: process.env.REACT_APP_BASE_URL_DEV, // Default to dev
  };

  const baseURL = `${
    baseURLMapping[env] || process.env.REACT_APP_BASE_URL_DEV
  }/api`;

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("access")}`,
  };

  const axiosInstance = axios.create({
    baseURL,
    headers,
  });

  // Test connection

  const getTestConnection = async () => {
    try {
      const response = await axiosInstance.get("/test-connection/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Common use

  const getCollections = async () => {
    try {
      const response = await axiosInstance.get("/collection/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getZones = async (active_only = false) => {
    try {
      if (active_only) {
        const response = await axiosInstance.get("/zone/active_zones/");
        return response;
      } else {
        const response = await axiosInstance.get("/zone/");
        return response;
      }
    } catch (error) {
      throw error;
    }
  };

  const getLines = async () => {
    try {
      const response = await axiosInstance.get("/line/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getZoneConfigs = async () => {
    try {
      const response = await axiosInstance.get("/zone-configuration/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  const createZoneConfig = async (data) => {
    try {
      const response = await axiosInstance.post("/zone-configuration/", data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateZoneConfigs = async (id, data) => {
    try {
      const response = await axiosInstance.patch(`/zone-configuration/${id}/`, {
        params: data,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateZoneCoordinates = async (zone) => {
    try {
      const response = await axiosInstance.patch(
        `/zone/update_zone_coordinates/`,
        {
          id: zone.id,
          coordinates: zone.coordinates,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateLineCoordinates = async (line) => {
    try {
      const response = await axiosInstance.patch(
        `/line/update_line_coordinates/`,
        {
          id: line.id,
          coordinates: line.coordinates,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deactivateOtherConfigs = async () => {
    try {
      const response = await axiosInstance.post(
        "/zone-configuration/deactivate-other-configs/"
      );
      return response.data;
    } catch (error) {
      throw new Error("Erro ao desativar configurações: " + error.message);
    }
  };

  const removeZoneFromConfig = async (zoneId) => {
    try {
      const response = await axiosInstance.post(
        `/zone/remove_zone_from_config/`,
        {
          id: zoneId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const removeLineFromConfig = async (lineId) => {
    try {
      const response = await axiosInstance.post(
        `/line/remove_line_from_config/`,
        {
          id: lineId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteLine = async (id) => {
    try {
      const response = await axiosInstance.delete(`/line/${id}/`);
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const duplicateConfig = async (configId) => {
    try {
      const response = await axiosInstance.post(
        `/zone-configuration/${configId}/duplicate/`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Verifiers

  const getCheckEventsContent = async () => {
    try {
      const response = await axiosInstance.get(
        "/events-register/check_content/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCheckCollectionsChangeContent = async () => {
    try {
      const response = await axiosInstance.get(
        "/collections-change-register/check_content/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCheckFlowDetectionsContent = async () => {
    try {
      const response = await axiosInstance.get(
        "/flow-detections/check_content/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // FlowDetections

  const getFlowDetectionsLastRecord = async () => {
    try {
      const response = await axiosInstance.get(
        "/flow-detections/get_last_record"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getProcessedData = async (params) => {
    try {
      const response = await axiosInstance.get(
        "/flow-detections/get_processed_data/",
        { params: params }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getDailyOccupation = async (params) => {
    try {
      const response = await axiosInstance.get(
        "/flow-detections/get_daily_occupation/",
        { params: params }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getHeatMapByDate = async (params) => {
    try {
      const response = await axiosInstance.get(
        "/camera-heat-map-images/getByDate/",
        {
          params: params,
          responseType: "text",
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // CollectionsChange

  const getEventsTableData = async (params) => {
    try {
      const response = await axiosInstance.get(
        "/events-analysis/get_table_data/",
        {
          params: params,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getEventsKPIData = async (params) => {
    try {
      const response = await axiosInstance.get(
        "events-analysis/retrieve_object_data/",
        {
          params: params,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getEventsLastChange = async () => {
    try {
      const response = await axiosInstance.get(
        "events-analysis/get_last_record/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getTotalInCount = async (params) => {
    try {
      const response = await axiosInstance.get(
        "entrance-record/get_total_in_count/",
        {
          params: params,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getZoneAveragePermanence = async (params) => {
    try {
      const response = await axiosInstance.get(
        "/zone-entrance-record/get_average_permanence/",
        {
          params: params,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // PeakHours

  // EventsManager
  const postEvent = async (params) => {
    try {
      const response = await axiosInstance.post("/events-register/", {
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getZoneById = async (id) => {
    try {
      const response = await axiosInstance.get(`/zone/${id}/`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const patchZoneCollection = async (zoneId, newCollection) => {
    try {
      const collectionIds = newCollection.join(",");

      const response = await axiosInstance.patch(
        `zone/${zoneId}/update_zone_collection/?new_collection=${encodeURIComponent(
          collectionIds
        )}`
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  const postCollectionChange = async (params) => {
    try {
      const response = await axiosInstance.post(
        "/collections-change-register/",
        params
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postCollection = async (params) => {
    try {
      const response = await axiosInstance.post("/collection/", {
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Edit Zones
  const postZones = async (data) => {
    try {
      const response = await axiosInstance.post("/zone/create_zones/", {
        data: data,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postLines = async (data) => {
    try {
      const response = await axiosInstance.post("/line/create_lines/", {
        data: data,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postConfigs = async (data) => {
    try {
      const response = await axiosInstance.post("/zone-configuration/", {
        params: data,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCameras = async () => {
    try {
      const response = await axiosInstance.get("/cameras/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateZonesInCameras = async (data) => {
    try {
      const response = await axiosInstance.patch(
        "/cameras/update_zones_in_cameras/",
        {
          data: data,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateLinesInCameras = async (data) => {
    try {
      const response = await axiosInstance.patch(
        "/cameras/update_lines_in_cameras/",
        { data }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const createCamera = async (data, file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("data", JSON.stringify(data));

    try {
      const response = await axiosInstance.post("/cameras/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const removeCamera = async (id) => {
    try {
      const response = await axiosInstance.delete(`/cameras/${id}/`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const editCamera = async (id, data) => {
    const formData = new FormData();
    formData.append("data", JSON.stringify(data));

    try {
      const response = await axiosInstance.patch(`/cameras/${id}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const countCameras = async () => {
    try {
      const response = await axiosInstance.get("/cameras/count/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  const retrieve_zones_by_config = async (config_id) => {
    try {
      const response = await axiosInstance.get(
        `/zone-configuration/retrieve-zones-by-config/`,
        {
          params: { config_id: config_id },
        }
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.error("Erro ao carregar zonas:", error);
    }
  };

  const retrieve_lines_by_config = async (config_id) => {
    try {
      const response = await axiosInstance.get(
        `/zone-configuration/retrieve-lines-by-config/`,
        {
          params: { config_id: config_id },
        }
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.error("Erro ao carregar zonas:", error);
    }
  };

  // Peak Hours

  const getPeakHoursKpiTableData = async (params) => {
    try {
      const response = await axiosInstance.get(
        "/flow-detections/get_peak_hours_kpi_and_tables",
        {
          params: params,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getPeakHoursMatrixBarGraphData = async (params) => {
    try {
      const response = await axiosInstance.get(
        "/flow-detections/get_matrix_and_bar_graph",
        {
          params: params,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // ZonesCollections

  const postCollectionsZonesCameras = async (collectionsFile, zonesFile) => {
    const formData = new FormData();
    formData.append("collections", collectionsFile);
    formData.append("zones", zonesFile);

    try {
      const response = await axiosInstance.post(
        "/postCollectionsZonesCameras/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getZoneActualCollection = async (zoneId) => {
    try {
      const response = await axiosInstance.get(
        `/zones-collections/${zoneId}/get_zone_actual_collection/`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const patchZoneCollectionUnion = async (zoneId, params) => {
    try {
      const response = await axiosInstance.patch(
        `/zones-collections/${zoneId}/`,
        null,
        {
          params: params,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postZonesCollections = async (params) => {
    try {
      const response = await axiosInstance.post("/zones-collections/", null, {
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postStandardSchedule = async (data) => {
    try {
      const response = await axiosInstance.post("/standard-schedule/", data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postSpecialSchedule = async (data) => {
    try {
      const response = await axiosInstance.post("/special-schedule/", data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getWeeklySchedule = async (date) => {
    try {
      const response = await axiosInstance.get(
        "/standard-schedule/get_branch_schedule/",
        {
          params: {
            date: date,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const existsZoneWithoutCollection = async () => {
    try {
      const response = await axiosInstance.get(
        "/zones-collections/exists_zone_without_collection/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const existsDaysWithoutSchedule = async ({ storeId }) => {
    try {
      const response = await axiosInstance.get(
        "/standard-schedule/exists_days_without_schedule/",
        {
          params: {
            store: storeId,
          },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getZonesCardsKpisData = async (params) => {
    try {
      const response = await axiosInstance.get("/home/zones_home_kpis/", {
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getUnlinkedCollections = async () => {
    try {
      const response = await axiosInstance.get(
        "/home/check_unlinked_collections/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCollectionsWithoutRevenue = async () => {
    try {
      const response = await axiosInstance.get(
        "/home/check_collections_without_revenue/"
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCollectionsCardsKpisData = async (params) => {
    try {
      const response = await axiosInstance.get("/home/collections_home_kpis/", {
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getClients = async () => {
    try {
      const response = await axiosInstance.get("/client/");
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getBranches = async (clientId) => {
    try {
      const response = await axiosInstance.get("/branch/", {
        params: {
          clientId: clientId,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postClients = async (name) => {
    try {
      const response = await axiosInstance.post("/client/", {
        name: name,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postBranch = async (
    branchName,
    clientId,
    storeCode,
    branchIP = null
  ) => {
    try {
      const payload = {
        name: branchName,
        client: clientId,
      };

      if (storeCode) {
        payload.store_code = storeCode;
      }

      if (branchIP) {
        payload.ip_address = branchIP;
      }

      const response = await axiosInstance.post("/branch/", payload);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postUserBranch = async (userId, branchId) => {
    try {
      const response = await axiosInstance.post("/user-branch/", {
        user: userId,
        store: branchId,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const patchBranchData = async (id, updatedFields) => {
    try {
      const response = await axiosInstance.patch(
        `/branch/${id}/`,
        updatedFields
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const patchClientData = async (id, updatedFields) => {
    try {
      const response = await axiosInstance.patch(
        `/client/${id}/`,
        updatedFields
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteClientData = async (id) => {
    try {
      const response = await axiosInstance.delete(`/client/${id}/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const deleteBranchData = async (id) => {
    try {
      const response = await axiosInstance.delete(`/branch/${id}/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const patchUserBranch = async ({ userId, branchIds, clientId = null }) => {
    try {
      const payload = {
        user_id: userId,
        branch_ids: branchIds,
      };
      if (clientId !== null) {
        payload.client_id = clientId;
      }

      const { data } = await axiosInstance.post("/user-branch/sync/", payload);
      return data;
    } catch (error) {
      throw error;
    }
  };

  return {
    getTestConnection,
    getCollections,
    getZones,
    getLines,
    getZoneConfigs,
    createZoneConfig,
    updateZoneConfigs,
    updateZoneCoordinates,
    updateLineCoordinates,
    deactivateOtherConfigs,
    removeZoneFromConfig,
    removeLineFromConfig,
    deleteLine,
    duplicateConfig,
    getProcessedData,
    getDailyOccupation,
    getHeatMapByDate,
    getFlowDetectionsLastRecord,
    getEventsTableData,
    getEventsKPIData,
    getEventsLastChange,
    getZoneAveragePermanence,
    getTotalInCount,
    postEvent,
    getZoneById,
    patchZoneCollection,
    postCollectionChange,
    postCollection,
    postConfigs,
    getCheckEventsContent,
    getCheckCollectionsChangeContent,
    getCheckFlowDetectionsContent,
    postZones,
    postLines,
    getCameras,
    countCameras,
    updateZonesInCameras,
    updateLinesInCameras,
    getPeakHoursKpiTableData,
    getPeakHoursMatrixBarGraphData,
    createCamera,
    removeCamera,
    editCamera,
    postCollectionsZonesCameras,
    retrieve_zones_by_config,
    retrieve_lines_by_config,
    getZoneActualCollection,
    patchZoneCollectionUnion,
    postZonesCollections,
    postStandardSchedule,
    postSpecialSchedule,
    getWeeklySchedule,
    existsZoneWithoutCollection,
    existsDaysWithoutSchedule,
    getZonesCardsKpisData,
    getUnlinkedCollections,
    getCollectionsWithoutRevenue,
    getCollectionsCardsKpisData,
    getClients,
    getBranches,
    postUserBranch,
    postClients,
    postBranch,
    patchBranchData,
    patchClientData,
    deleteClientData,
    deleteBranchData,
    patchUserBranch,
  };
};

export default useApi;
